import { useState } from "react";
import { Wrench, Pencil, Trash2 } from "lucide-react";
import styles from "./SupportPackages.module.css";
import SupportServiceModal from "../../../components/System&Settings/SupportPackages/SupportServiceModal";
import SelectBox from "../../../components/BaseComponents/SelectBox/SelectBox";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import { useToast } from "../../../context/ToastContext";
import {
  useSupportServicesList,
  useMutation,
  createSupportService,
  updateSupportService,
  deleteSupportService,
} from "../../../api/support-packages/useSupportPackages";

/**
 * Support Services overview (Support spec v1.0 §6.3).
 *
 * §6.3 fixes the columns exactly: Service Name, Code, Customer Text, Status, Actions, with
 * Search and Status filters and a + Add Service action. It also says this list "does not
 * require a Sort Order field" - ordering is per level, not global, so there is deliberately no
 * drag handle here.
 *
 * §4 is the reason this screen exists at all: services are a central dynamic list that SUNSKY
 * manages "without development work", carrying no price and no product type of their own.
 */
const SupportServices = () => {
  const { showToast } = useToast();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const { items, total, loading, error, refetch } = useSupportServicesList({
    search: search || undefined,
    status: status || undefined,
    limit: 200,
  });

  const create = useMutation(createSupportService);
  const update = useMutation(updateSupportService);
  const remove = useMutation(deleteSupportService);

  const handleSave = async (payload) => {
    try {
      if (editing) await update.execute(editing.id, payload);
      else await create.execute(payload);
      showToast(editing ? "Support service updated" : "Support service created", "success");
      setModalOpen(false);
      setEditing(null);
      refetch();
    } catch (err) {
      // The server's message is the useful one here: a duplicate code, or a code frozen because
      // the service has already been sold (§7).
      showToast(err.message, "error");
    }
  };

  const handleDelete = async () => {
    try {
      await remove.execute(toDelete.id);
      showToast("Support service deleted", "success");
      setToDelete(null);
      refetch();
    } catch (err) {
      // §7 blocks deleting anything that has been used, and the reason matters to the operator,
      // so it is shown rather than swallowed into "failed to delete".
      showToast(err.message, "error");
      setToDelete(null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>
          <Wrench size={20} />
          Support Services
        </div>
        <button
          className={styles.add}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          disabled={loading}
        >
          + Add Service
        </button>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>ℹ</span>
        <span>
          Services are the central list you assign to support levels. A service has no price of
          its own: whether it is included, paid or not included, and any fee, is decided per
          support level.
        </span>
      </div>

      <div className={styles.filterBar}>
        <form
          className={styles.searchForm}
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(searchInput.trim());
          }}
        >
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by name, code or customer text…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            disabled={loading}
          />
          <button type="submit" className={styles.addbtn} disabled={loading}>
            Search
          </button>
        </form>
        <SelectBox
          placeholder="Status: All"
          value={status}
          options={[
            { label: "All", value: "" },
            { label: "Active", value: "active" },
            { label: "Inactive", value: "inactive" },
          ]}
          disabled={loading}
          onChange={(e) => setStatus(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading support services…</div>
      ) : (
        <>
          <div className={styles.resultsSummary}>
            Showing {items.length} of {total} support services
          </div>
          <div className={styles.tableSection}>
            <div className={styles.tableCard}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>SERVICE NAME</th>
                    <th>CODE</th>
                    <th>CUSTOMER TEXT</th>
                    <th>STATUS</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={styles.noResults}>
                        No support services found.
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.id}>
                        <td className={styles.levelName}>{row.name}</td>
                        <td className={styles.code}>{row.code}</td>
                        <td className={styles.muted}>{row.customerText}</td>
                        <td>
                          <span className={row.active ? styles.statusOn : styles.statusOff}>
                            {row.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className={styles.actionsCell}>
                          <button
                            className={styles.iconBtn}
                            title="Edit"
                            onClick={() => {
                              setEditing(row);
                              setModalOpen(true);
                            }}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            className={`${styles.iconBtn} ${styles.danger}`}
                            title="Delete"
                            onClick={() => setToDelete(row)}
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {modalOpen && (
        <SupportServiceModal
          service={editing}
          saving={create.loading || update.loading}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSave={handleSave}
        />
      )}

      {toDelete && (
        <DeleteConfirmModal
          isOpen
          title="Delete support service"
          message={`Delete "${toDelete.name}"? This cannot be undone. A service that has been used on a booking cannot be deleted - deactivate it instead.`}
          loading={remove.loading}
          onCancel={() => setToDelete(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default SupportServices;
